import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { sendMessage, loadHistory, clearConversation } from '../../actions/ChatAction';
import ChatMessage from './ChatMessage';
import './chatbot.css';

/**
 * Janela principal do chat
 */
const ChatWindow = ({ usuario, messages, isTyping, error, onClose, sendMessage, loadHistory, clearConversation }) => {
  const [inputValue, setInputValue] = useState('');
  const [showError, setShowError] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Focar no input ao abrir
    if (inputRef.current) {
      inputRef.current.focus();
    }

    // Carregar histórico
    if (usuario && usuario.id) {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    // Mostrar erro quando houver
    if (error) {
      setShowError(true);
      // Esconder erro após 5 segundos
      const timer = setTimeout(() => {
        setShowError(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    sendMessage(trimmed);
    setInputValue('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickQuestions = [
    { icon: 'fa-calendar', text: 'Quando é a entrega?', message: 'Quando é a data de entrega?' },
    { icon: 'fa-shopping-bag', text: 'O que eu comprei?', message: 'Quais itens eu comprei?' },
    { icon: 'fa-info-circle', text: 'Status do contrato', message: 'Qual o status do meu contrato?' },
    { icon: 'fa-map-marker', text: 'Endereço de entrega', message: 'Qual o endereço de entrega?' }
  ];

  const handleQuickQuestion = (message) => {
    sendMessage(message);
  };

  const handleClearChat = () => {
    clearConversation();
  };

  return (
    <div className="chatbot-window">
      {/* Header */}
      <div className="chatbot-header">
        <div className="chatbot-header-left">
          <div className="chatbot-avatar">
            <i className="fa fa-robot" />
          </div>
          <div className="chatbot-header-info">
            <h4>Assistente Virtual (Alpha)</h4>
            <span className="chatbot-status">
              <span className="status-dot"></span>
              Online
            </span>
          </div>
        </div>
        <div className="chatbot-header-actions">
          <button
            className="chatbot-header-btn"
            onClick={handleClearChat}
            title="Limpar conversa"
          >
            <i className="fa fa-trash" />
          </button>
          <button
            className="chatbot-header-btn"
            onClick={onClose}
            title="Fechar"
          >
            <i className="fa fa-times" />
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {showError && error && (
        <div className="chatbot-error-banner">
          <i className="fa fa-exclamation-triangle" />
          <span>{error}</span>
          <button
            className="error-close-btn"
            onClick={() => setShowError(false)}
            aria-label="Fechar erro"
          >
            <i className="fa fa-times" />
          </button>
        </div>
      )}

      {/* Messages Area */}
      <div className="chatbot-messages">
        {/* Mensagem de boas-vindas */}
        {messages.length === 0 && (
          <div className="chatbot-welcome">
            <div className="welcome-icon">👋</div>
            <h3>Olá, {usuario?.nome || 'formando'}!</h3>
            <p>Sou o assistente virtual da Alpha Convites. Como posso ajudar você hoje?</p>
          </div>
        )}

        {/* Mensagens */}
        {messages.map((msg, index) => (
          <ChatMessage key={index} message={msg} />
        ))}

        {/* Typing indicator */}
        {isTyping && (
          <div className="chatbot-message bot">
            <div className="message-avatar">
              <i className="fa fa-robot" />
            </div>
            <div className="chatbot-typing">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Questions */}
      {messages.length <= 1 && (
        <div className="chatbot-quick-questions">
          <div className="quick-questions-label">Perguntas rápidas:</div>
          <div className="quick-questions-grid">
            {quickQuestions.map((q, index) => (
              <button
                key={index}
                className="quick-question-btn"
                onClick={() => handleQuickQuestion(q.message)}
              >
                <i className={`fa ${q.icon}`} />
                <span>{q.text}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="chatbot-input-area">
        <div className="chatbot-input-wrapper">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite sua mensagem..."
            rows={1}
            disabled={isTyping}
          />
          <button
            className="chatbot-send-btn"
            onClick={handleSend}
            disabled={!inputValue.trim() || isTyping}
          >
            <i className="fa fa-paper-plane" />
          </button>
        </div>
      </div>
    </div>  
  );
};

const mapStateToProps = (state) => ({
  usuario: state.usuario.autenticado,
  messages: state.chatbot.messages,
  isTyping: state.chatbot.isTyping,
  error: state.chatbot.error
});

const mapDispatchToProps = {
  sendMessage,
  loadHistory,
  clearConversation
};

export default connect(mapStateToProps, mapDispatchToProps)(ChatWindow);
